// Params that should be captured (so the app can read them once) and then
// stripped from the address bar before any analytics SDK reads
// `window.location`. `token` is security-sensitive; `adminUrl` is just noise
// that we strip and persist so impersonation can be ended later.
const SENSITIVE_PARAMS = ["token", "adminUrl"];

/**
 * Values captured from the browser URL by `stripSensitiveParamsFromBrowserUrl`
 * before they are scrubbed. Keyed by parameter name.
 */
const _capturedParams: Record<string, string> = {};

/**
 * Retrieve a sensitive parameter value that was captured (and then stripped)
 * by `stripSensitiveParamsFromBrowserUrl`.  Returns `null` if the parameter
 * was never present.
 */
export function getCapturedSensitiveParam(key: string): string | null {
  return _capturedParams[key] ?? null;
}

/**
 * Strip security-sensitive query parameters (e.g. impersonation tokens) from a
 * URL string.
 *
 * Always returns a **relative** URL: pathname + optional `?search` + optional
 * `#hash` (never a scheme/host), so callers like Sentry `beforeSendTransaction`
 * get consistent transaction names whether the input was a full URL or a path.
 *
 * Works with both full URLs and path-only strings
 * (`/impersonate?token=abc`).
 */
export function sanitizeUrlParams(raw: string): string {
  try {
    const url = new URL(raw, "https://placeholder.invalid");

    for (const key of SENSITIVE_PARAMS) {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
      }
    }

    // Also strip from hash fragments (`#token=...`)
    if (url.hash) {
      const hashContent = url.hash.replace(/^#/, "");
      const hashParams = new URLSearchParams(hashContent);
      for (const key of SENSITIVE_PARAMS) {
        if (hashParams.has(key)) {
          hashParams.delete(key);
        }
      }
      url.hash = hashParams.toString() ? `#${hashParams.toString()}` : "";
    }

    const search = url.searchParams.toString();
    const hash = url.hash;
    let result = url.pathname;
    if (search) result += `?${search}`;
    if (hash) result += hash;
    return result;
  } catch {
    return raw;
  }
}

/**
 * If the current browser URL contains any sensitive query parameters or hash
 * params, capture their values, then replace the address-bar URL immediately
 * (synchronous, no navigation) and return the sanitised pathname+search.
 *
 * This must run **before** any analytics SDK reads `window.location`.
 * Captured values can later be retrieved with `getCapturedSensitiveParam`.
 */
export function stripSensitiveParamsFromBrowserUrl(): string {
  // Capture values from both query-string and hash before stripping.
  const searchParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(
    window.location.hash.replace(/^#/, ""),
  );
  for (const key of SENSITIVE_PARAMS) {
    const fromHash = hashParams.get(key);
    if (fromHash) {
      _capturedParams[key] = fromHash;
    } else {
      const fromSearch = searchParams.get(key);
      if (fromSearch) {
        _capturedParams[key] = fromSearch;
      }
    }
  }

  const current =
    window.location.pathname + window.location.search + window.location.hash;
  const clean = sanitizeUrlParams(current);

  if (clean !== current) {
    window.history.replaceState(null, "", clean);
  }

  return clean;
}
